export enum BillingAction {
  READY_TO_PAY = "readyToPay",
  UPCOMING = "upcoming",
}

export enum StatementStatus {
  PAID = "PAID",
  READY_TO_PAY = "READY_TO_PAY",
  PAST_DUE = "PAST_DUE",
  FINAL_NOTICE = "FINAL_NOTICE",
  PRE_COLLECTION = "PRE_COLLECTION",
  PAYMENT_PENDING = "PAYMENT_PENDING",
  CLAIM_PENDING = "CLAIM_PENDING",
  PARTIAL_PENDING = "PARTIAL_PENDING",
  PAYMENT_PLAN = "PAYMENT_PLAN",
  NATERA_COLLECTIONS = "NATERA_COLLECTIONS",
}

export enum PastDueType {
  NO_PAST_DUE = "NO_PAST_DUE",
  PAST_DUE = "PAST_DUE",
  FINAL_NOTICE = "FINAL_NOTICE",
  PRE_COLLECTION_NOTICE = "PRE_COLLECTION_NOTICE",
}

export enum TestType {
  PANORAMA = "Panorama",
  HORIZON = "Horizon",
  EMPOWER = "Empower",
}

export type TestName = {
  fullName: string;
  shortName: string;
};

export enum PaymentDueStatus {
  VALID_DATE = "VALID_DATE",
  DUE_UPON_RECEIPT = "DUE_UPON_RECEIPT",
  DUE_NOW = "DUE_NOW",
  EMPTY = "EMPTY",
}

export enum ReviewAndPayStatus {
  PAYMENTS_WAITING = "PAYMENTS_WAITING",
  NOTHING_TO_PAY = "NOTHING_TO_PAY",
  PAYMENT_PENDINGS_ONLY = "PAYMENT_PENDINGS_ONLY",
}

export type PaymentDueInfo = {
  dueDate?: string;
  pastDueExists: PaymentDueStatus;
};

export type Item = {
  orderUid: string;
  lineItemUid: string;
  clinicId?: string;
  testName: TestName;
  balance: number;
  dueDate: PaymentDueInfo;
  dateOfService: string;
  statementStatus: StatementStatus;
  orderedBy: string;
  caseNumber: string;
  currency: string;
  hasPdf: boolean;
};

export type PaymentMethod = {
  brand: PaymentCardBrand;
  last4digits: string;
  expiry: string;
};

export type InstallmentItem = {
  numInstallments: number;
  currentInstallmentIndex: number;
  installmentAmount: number;
  dueDate: string;
  caseNumber: string;
  remainingBalance: number;
  paymentMethod: PaymentMethod;
  testName: string;
  paymentPlanId: string;
  status: BillingPaymentPlanStatus;
  currency: string;
};

export type Bill = Item | InstallmentItem;

export type GetItemsResponseData = {
  patientUid: string;
  patientName: string;
  totalBalance: number;
  balanceDateStatus: ReviewAndPayStatus;
  displayDate: PaymentDueInfo;
  pastDueExists: boolean;
  pastDueType: PastDueType;
  hasInternationalClinic: boolean;
  readyToPay: Item[];
  upcoming: Item[];
  readyToPayInstallments: InstallmentItem[];
  upcomingInstallments: InstallmentItem[];
};

export type GuestValidationResponseData = {
  token: string;
};

export type PublicGuestValidationResponsePatientData = {
  uid: string;
  firstName: string;
  lastName: string;
};

export type PublicGuestValidationResponseData = {
  patient: PublicGuestValidationResponsePatientData;
  token: string;
};

export enum PaymentCardBrand {
  AMEX = "amex",
  DINERS = "diners",
  DISCOVER = "discover",
  EFTPOS_AU = "eftpos_au",
  JCB = "jcb",
  LINK = "link",
  MASTERCARD = "mastercard",
  UNIONPAY = "unionpay",
  VISA = "visa",
  UNKNOWN = "unknown",
}

export type PaymentMethodSummary = {
  brand: PaymentCardBrand;
  last4digits: string;
  expiry: string;
};

export enum PaymentPlanInstallmentStatus {
  PAID = "PAID",
  UNPAID = "UNPAID",
  FAILED = "FAILED",
}

export type PaymentPlanDetailInstallment = {
  amount: number;
  dueDate: string;
  index: number;
  status: PaymentPlanInstallmentStatus;
  paidDate?: string;
};

export type PaymentPlanDetailResponseData = {
  currency: string;
  currentInstallmentIndex: number;
  numberOfInstallments: number;
  orderTotal: number;
  paidToDate: number;
  paymentDate: string;
  paymentMethodId: PaymentMethodSummary;
  planStartDate: string;
  remainingBalance: number;
  testName: string;
  numLineItems: number;
  installments: PaymentPlanDetailInstallment[];
  status: BillingPaymentPlanStatus;
  cancellationDate: string;
  paidFullBalance?: boolean;
  paidLater?: boolean;
};

export type PaymentPlanDetailItem = PaymentPlanDetailResponseData;

export type DOBData = {
  day: number;
  month: number;
  year: number;
};

export type GuestValidationInput = {
  token: string;
  dob: DOBData;
};

interface PublicGuestValidationInputDOB {
  day: string;
  month: string;
  year: string;
}

interface PublicGuestValidationInputUserName {
  firstName: string | null;
  lastName: string | null;
}

export interface PublicGuestValidationInputCaseNumber extends PublicGuestValidationInputDOB {
  caseNumber: string | null;
}

export interface PublicGuestValidationInputZipCode
  extends PublicGuestValidationInputDOB, PublicGuestValidationInputUserName {
  zipCode: string | null;
}

export interface PublicGuestValidationInputMobilePhone
  extends PublicGuestValidationInputDOB, PublicGuestValidationInputUserName {
  mobilePhone: string | null;
}

export interface PublicGuestValidationInputEmail
  extends PublicGuestValidationInputDOB, PublicGuestValidationInputUserName {
  email: string | null;
}

export type PublicGuestValidationInput =
  | PublicGuestValidationInputCaseNumber
  | PublicGuestValidationInputZipCode
  | PublicGuestValidationInputMobilePhone
  | PublicGuestValidationInputEmail;

export interface PaymentPlanDetailInput {
  paymentPlanId: string;
}

export type BillItem = {
  orderUid: string;
  lineItemUid: string;
};

type PaymentOrderItemPair = {
  bosOrderUid: string;
  bosOrderLineItemUid: string;
};

export type CreatePaymentInput = {
  currency: string;
  amount: number;
  itemList: PaymentOrderItemPair[];
};

export type CreatePaymentResponseData = {
  clientSecret: string;
  customerSessionSecret: string;
  paymentRequestId: string;
};

export type UpdatePaymentResponseData = {
  clientSecret: string;
  customerSessionSecret: string;
};

export type UpdatePaymentMethodInput = {
  paymentPlanId: string;
  setupIntentId: string;
};

export type ConfirmPaymentInput = {
  paymentRequestId: string;
  paymentIntentId: string;
};

export type CreatePaymentPlanInput = {
  currency: string;
  amount: number;
  itemList: PaymentOrderItemPair[];
  numberOfInstallment: number;
};

export type CreatePaymentPlanResponseData = {
  clientSecret: string;
  customerSessionSecret: string;
  paymentRequestId: string;
};

export type CreatePayFullBalanceResponseData = {
  clientSecret: string;
  customerSessionSecret: string;
  paymentRequestId: string;
};

export type ConfirmPaymentPlanInput = {
  paymentRequestId: string;
  paymentMethodId: string;
  setupIntentId: string;
};

export type ConfirmPayFullBalanceInput = {
  paymentPlanId: string;
  paymentRequestId: string;
  paymentIntentId: string;
};

export type HistoryRequestData = {
  pageSize: number;
  pageNumber: number;
  startDate?: string;
  endDate?: string;
  sortType?: string;
};

export type StatementsResponseData = {
  totalCount: number;
  items: StatementItem[];
};

export type PaymentsHistoryResponseData = {
  totalCount: number;
  items: PaymentHistoryItem[];
};

export type StatementItem = {
  bosOrderUid: string;
  bosLineItemUid: string;
  statementUid: string;
  testType: string;
  balance: number;
  caseNumber: string;
  dateOfService: string;
  orderedBy: string;
  hasPDF: boolean;
  paidStatus: StatementStatus;
  currency: string;
};

export type PaymentHistoryItem = {
  bosPaymentObjectId: string;
  lineItemUid: string;
  testName: TestName;
  caseNumber?: string;
  paidDate?: string;
  dateOfService: string;
  paymentMethod?: PaymentMethod;
  amount: number;
  currency: string;
  receiptId?: string;
  upsPaymentId?: string;
};

export type PaymentPlanPreviewRequestInput = {
  currency: string;
  amount: number;
  itemList: PaymentPlanItem[];
  numberOfInstallment: number;
};

export type PaymentPlanItem = {
  bosOrderUid: string;
  bosOrderLineItemUid: string;
};

export type PaymentPlanPreviewResponseData = {
  items: PaymentInstallmentItem[];
};

export type PaymentInstallmentItem = {
  indexes: number[];
  amount: number;
};

export type PaymentPlansRequestInput = {
  startDate?: string;
  endDate?: string;
  sort?: string;
  page?: string;
  limit?: string;
};

export enum BillingPaymentPlanStatus {
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
  COMPLETED = "COMPLETED",
  ACTIVE = "ACTIVE",
  ERROR = "ERROR",
}

export type PaymentPlanCardItem = {
  paymentPlanId: string;
  caseNumber: string;
  currency: string;
  dueDate: string;
  installmentAmount: number;
  paymentMethod?: PaymentMethod;
  remainingBalance: number;
  numInstallments: number;
  currentInstallmentIndex: number;
  status: BillingPaymentPlanStatus;
};

export type PaymentPlansResponseData = {
  items: PaymentPlanCardItem[];
  lastEvaluatedKey?: string;
  totalCount: number;
};

export type RefreshCTSTokenErrorData = {
  authMethod: AuthMethod;
  tokenizedLink?: string;
};

export class PostBillError extends Error {
  error: PostBillErrors;
  data?: Partial<RefreshCTSTokenErrorData>;

  constructor(
    errorType: PostBillErrors,
    data?: Partial<RefreshCTSTokenErrorData>
  ) {
    super(errorType);
    this.name = "PostBillError";
    this.error = errorType;
    this.data = data;
  }
}

export type ResponseData<T = void> = {
  message: string;
  data?: T;
  error?: PostBillErrors;
};

export type GetStatementRequestInput = {
  bosOrderUid: string;
  bosLineItemUid: string;
  statementUid?: string;
};

export type PDFResponse = ArrayBuffer | ResponseData;

export enum PostBillErrors {
  INTERNAL_SERVER_ERROR = "INTERNAL_SERVER_ERROR",
  PATIENT_NOT_FOUND = "PATIENT_NOT_FOUND",
  PATIENT_UID_NOT_MATCHED = "PATIENT_UID_NOT_MATCHED",
  ORDER_NOT_FOUND = "ORDER_NOT_FOUND",
  INCORRECT_DOB = "INCORRECT_DOB",
  CTS_TOKEN_EXPIRED = "CTS_TOKEN_EXPIRED",
  PATIENT_DOB_MISMATCH = "PATIENT_DOB_MISMATCH",
}

export enum PaymentPlan {
  None = 0,
  ThreeMonths = 3,
  SixMonths = 6,
  TwelveMonths = 12,
}

export enum InstallmentVisualStatus {
  PAID = "PAID",
  CURRENT = "CURRENT",
  UP_COMING = "UP_COMING",
  CANCELLED = "CANCELLED",
}

export enum AuthMethod {
  PUBLIC = "PUBLIC",
  TOKENIZED_LINK = "TOKENIZED_LINK",
  LOGIN = "LOGIN",
}

export type VerifyLoginResponseData = {
  token: string;
  displayBillingCenter: boolean;
};

export type SubmitSurveyInput = {
  eventType?: "view" | "submit";
  easeOfPaymentScore?: number | null;
  easeOfPaymentComment?: string | null;
  npsScore?: number | null;
};
